import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Calculator as CalcIcon, Eraser, Banknote, Handshake, Trees, Home, Copy, Sparkles } from "lucide-react";
import { PageTip } from "@/components/Onboarding";
import ArvFinder from "@/components/ArvFinder";

const fmt = (n) => "$" + (Math.round((n ?? 0) * 100) / 100).toLocaleString();
const num = (v) => {
  const n = parseFloat(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const CALC_BLURB = {
  cash: "Straight cash assignment: 70% of ARV minus repairs minus your fee.",
  creative: "Creative finance: pay above asking over time, or take over the existing loan, structured so an end buyer still cash flows.",
  land: "Vacant land: priced off what builders in the area pay for dirt.",
  zillow: "Ballpark only: a Zestimate doesn't know the condition, so use it to start the repair conversation.",
};

const TABS = [
  { id: "cash", label: "Cash", icon: Banknote },
  { id: "creative", label: "Creative Finance", icon: Handshake },
  { id: "land", label: "Vacant Land", icon: Trees },
  { id: "zillow", label: "Zillow", icon: Home },
];

export default function Calculator() {
  const [params] = useSearchParams();
  const tab = params.get("tab") || "cash";
  const current = TABS.find((t) => t.id === tab) || TABS[0];

  useEffect(() => {
    gutCtx.address = "";
  }, [tab]);

  return (
    <div data-testid="calculator-page" className="max-w-3xl mx-auto">
      <div className="mb-8 text-center">
        <div className="section-tag mb-3 inline-flex"><span className="dot" /> Run your numbers</div>
        <h1 className="h-display text-4xl" data-testid="calculator-title">{current.label} Calculator</h1>
      </div>

      <PageTip flag="calculator" title={`${current.label} calculator`}>
        {CALC_BLURB[tab]}{" "}
        {tab === "creative"
          ? "Seller Finance gives you a price plus a payment structure; Mortgage Takeover gives you the cash you need to step into the loan."
          : "You get a Max Offer plus a 3 tier range. Open at Aggressive so you have room to come up."}{" "}
        Use &ldquo;Get a gut check&rdquo; for an AI read on the spread before you call.
      </PageTip>

      {tab === "cash" && <CashCalc />}
      {tab === "creative" && <CreativeCalc />}
      {tab === "land" && <LandCalc />}
      {tab === "zillow" && <ZillowCalc />}
    </div>
  );
}

function Field({ label, value, onChange, testid, text }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <input
        inputMode={text ? undefined : "decimal"}
        className={text ? "input-base" : "input-base font-mono"}
        placeholder={text ? "" : "0"}
        value={value}
        onChange={(e) => onChange(text ? e.target.value : e.target.value.replace(/[^\d.,]/g, ""))}
        data-testid={testid}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid, hint }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <select className="input-base" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      {hint && <p className="text-xs mt-1.5" style={{ color: "var(--text-dim)" }}>{hint}</p>}
    </div>
  );
}

function Actions({ onCalc, onClear, prefix }) {
  return (
    <div className="flex gap-3 mt-6">
      <button onClick={onCalc} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid={`${prefix}-calculate`}>
        <CalcIcon size={15} /> Calculate
      </button>
      <button onClick={onClear} className="btn-ghost flex items-center gap-2 px-5" data-testid={`${prefix}-clear`}>
        <Eraser size={14} /> Clear
      </button>
    </div>
  );
}

const PUSHBACK =
  "I hear you. Let me take that back to my underwriting team and see what I can do. No promises, but I'll go to bat for you. What number would make this work for you?";

/** "Hey Mike, ..." / "Hey, ..." and "your place on Elm St" / "your place" */
const hey = (seller) => (seller.trim() ? `Hey ${seller.trim()}, ` : "Hey, ");
const place = (street, word = "place") => (street.trim() ? `your ${word} on ${street.trim()}` : `your ${word}`);

function SellerFields({ seller, setSeller, street, setStreet, prefix }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4 mb-4">
      <Field label="Seller first name (optional)" value={seller} onChange={setSeller} testid={`${prefix}-seller-name`} text />
      <Field label="Street name (optional)" value={street} onChange={setStreet} testid={`${prefix}-street`} text />
    </div>
  );
}

const GUT_KEYS = { market: "wu_gut_market", exit: "wu_gut_exit" };

const CASH_EXITS = [
  ["assignment to a flipper", "Assign to a flipper"],
  ["assignment to a landlord / buy and hold buyer", "Assign to a landlord"],
  ["double close", "Double close"],
  ["novation / listing exit", "Novation or listing exit"],
  ["wholetail", "Wholetail"],
  ["not sure yet", "Not sure yet"],
];
const CREATIVE_EXITS = [
  ["assignment to a landlord / buy and hold buyer", "Assign to a landlord"],
  ["assignment to a tenant buyer on a rent to own", "Assign to a tenant buyer"],
  ["hold it as a rental myself", "Hold it as a rental"],
  ["novation / listing exit", "Novation or listing exit"],
  ["not sure yet", "Not sure yet"],
];
const gutCtx = { address: "" };
const gutKey = (k, prefix) =>
  k === "exit" ? `${GUT_KEYS.exit}_${prefix === "creative" || prefix === "subto" ? "creative" : "cash"}` : GUT_KEYS[k];
const readGut = (k, prefix) => {
  if (k === "address") return gutCtx.address;
  try {
    return localStorage.getItem(gutKey(k, prefix)) || "";
  } catch {
    return "";
  }
};
const writeGut = (k, v, prefix) => {
  if (k === "address") {
    gutCtx.address = v;
    return;
  }
  try {
    localStorage.setItem(gutKey(k, prefix), v);
  } catch {
    /* private mode */
  }
};

function ResultPanel({ offer, range, script, aiPayload, prefix, children, title = "Max Offer" }) {
  const [ai, setAi] = useState(null);
  const [busy, setBusy] = useState(false);
  const [market, setMarketState] = useState(() => readGut("market", prefix));
  const [address, setAddressState] = useState(() => readGut("address", prefix));
  const [exitStrategy, setExitState] = useState(() => readGut("exit", prefix));
  const setMarket = (v) => { setMarketState(v); writeGut("market", v, prefix); };
  const setAddress = (v) => { setAddressState(v); writeGut("address", v, prefix); };
  const setExitStrategy = (v) => { setExitState(v); writeGut("exit", v, prefix); };
  const exitOptions = prefix === "creative" || prefix === "subto" ? CREATIVE_EXITS : CASH_EXITS;

  const askAi = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/calculator/ai-review", {
        ...aiPayload,
        market: market.trim(),
        address: address.trim(),
        exit_strategy: exitStrategy,
      });
      setAi(data.review);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Team review failed");
    } finally {
      setBusy(false);
    }
  };

  const copyScript = async () => {
    try {
      await navigator.clipboard.writeText(script);
      toast.success("Offer script copied. Paste it to your seller");
    } catch {
      toast.error("Couldn't copy. Long press to copy manually");
    }
  };

  return (
    <div className="mt-6" data-testid={`${prefix}-result`}>
      <div className="p-6 rounded-xl text-center" style={{ background: "var(--lime-glow)", border: "1px solid var(--border-lime)" }}>
        <div className="font-mono text-xs uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>{title}</div>
        <div className="font-mono text-4xl" style={{ color: "var(--lime)" }}>{fmt(offer)}</div>
      </div>

      {range && (
        <div className="grid grid-cols-3 gap-3 mt-3" data-testid={`${prefix}-range`}>
          {range.map((r, i) => (
            <div
              key={r.label}
              className="p-3 rounded-2xl text-center"
              style={{
                background: i === 1 ? "var(--lime-glow)" : "var(--surface2)",
                border: i === 1 ? "1px solid var(--border-lime)" : "1px solid var(--border)",
              }}
            >
              <div className="font-mono text-[10px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                {r.label} · {r.note}
              </div>
              <div className="font-mono text-base mt-1" style={{ color: i === 1 ? "var(--lime)" : "var(--text)" }}>
                {fmt(r.value)}
              </div>
            </div>
          ))}
        </div>
      )}

      {children}

      <div className="mt-4 p-4 rounded-xl" style={{ background: "var(--surface2)", border: "1px solid var(--border)" }} data-testid={`${prefix}-script-box`}>
        <div className="flex items-center justify-between gap-3 mb-2">
          <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
            Your offer script
          </span>
          <button onClick={copyScript} className="flex items-center gap-1.5 font-mono text-xs px-3 py-1.5 rounded-full transition" style={{ color: "var(--lime)", border: "1px solid var(--border-lime)", background: "var(--lime-glow)" }} data-testid={`${prefix}-copy-script`}>
            <Copy size={12} /> Copy
          </button>
        </div>
        <p className="text-sm leading-relaxed" style={{ color: "var(--text)" }} data-testid={`${prefix}-script-text`}>
          “{script}”
        </p>
        <div className="mt-3 pt-3" style={{ borderTop: "1px solid var(--border)" }}>
          <div className="font-mono text-[11px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>
            If they push back
          </div>
          <p className="text-sm leading-relaxed" style={{ color: "var(--text-muted)" }} data-testid={`${prefix}-pushback-text`}>
            “{PUSHBACK}”
          </p>
          {range && range.length === 3 && (
            <p className="text-xs mt-2" style={{ color: "var(--text-dim)" }}>
              Then come up to {range[1].label}, then {range[2].label}. {title} is your ceiling.
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 p-4 rounded-xl" style={{ background: "var(--surface2)", border: "1px solid var(--border)" }}>
        <div className="font-mono text-[10px] uppercase tracking-wider mb-3" style={{ color: "var(--text-muted)" }}>
          Context for the gut check
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <input
            className="input-base text-sm"
            placeholder="City, state (e.g. Denver, CO)"
            value={market}
            onChange={(e) => setMarket(e.target.value)}
            data-testid={`${prefix}-market`}
          />
          <input
            className="input-base text-sm"
            placeholder="Address or ZIP (optional)"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            data-testid={`${prefix}-address`}
          />
          <select
            className="input-base text-sm"
            value={exitStrategy}
            onChange={(e) => setExitStrategy(e.target.value)}
            style={{ background: "var(--surface)", color: "var(--text)" }}
            data-testid={`${prefix}-exit-strategy`}
          >
            <option value="">Exit strategy...</option>
            {exitOptions.map(([v, label]) => (
              <option key={v} value={v}>{label}</option>
            ))}
          </select>
        </div>
        <p className="text-[11px] mt-2" style={{ color: "var(--text-dim)" }}>
          Optional, but it stops the AI guessing. The standard it judges you against changes with the market and the exit.
        </p>
      </div>

      <div className="mt-4">
        <button onClick={askAi} disabled={busy} className="btn-ghost w-full flex items-center justify-center gap-2 text-sm" style={{ color: "var(--lime)" }} data-testid={`${prefix}-ask-ai`}>
          {busy ? <span className="spinner" /> : <Sparkles size={14} />}
          {busy ? "Thinking..." : "Get a gut check"}
        </button>
      </div>

      {ai && (
        <div className="ai-box mt-4" data-testid={`${prefix}-ai-review`}>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={14} style={{ color: "var(--lime)" }} />
            <span className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--lime)" }}>AI gut check</span>
          </div>
          <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--text)" }}>
            {String(ai).replace(/\*\*/g, "").replace(/^#{1,6}\s*/gm, "")}
          </p>
        </div>
      )}
    </div>
  );
}

function CashCalc() {
  const { user } = useAuth();
  const [arv, setArv] = useState("");
  const [rehab, setRehab] = useState("");
  const [fee, setFee] = useState("");
  const [seller, setSeller] = useState("");
  const [street, setStreet] = useState("");
  const [result, setResult] = useState(null);

  const calc = () => {
    const a = num(arv), r = num(rehab), f = Math.max(0, num(fee));
    const offers = [0.65, 0.70, 0.75].map((p) => a * p - r - f);
    setResult({
      offer: offers[1],
      range: [
        { label: "Aggressive", note: "65%", value: offers[0] },
        { label: "Standard", note: "70%", value: offers[1] },
        { label: "Stretch", note: "75%", value: offers[2] },
      ],
      script: `${hey(seller)}I've got some good news. I sent ${place(street)} over to my underwriting team, and they got you approved for an offer. They came back at ${fmt(offers[0])} cash. That's as-is, so you don't have to fix anything or clean anything out, and there's no agent fees. We can close on your timeline. How does that sound to you?`,
      aiPayload: { calc_type: "cash", inputs: { ARV: a, Repairs: r, "Assignment Fee": f }, max_offer: offers[1] },
    });
  };
  const clear = () => { setArv(""); setRehab(""); setFee(""); setSeller(""); setStreet(""); setResult(null); };

  return (
    <div className="surface-card p-6 sm:p-8" data-testid="cash-calc">
      {user?.is_admin && <ArvFinder onArvChange={setArv} />}
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="ARV ($)" value={arv} onChange={setArv} testid="cash-arv" />
        <Field label="Repairs ($)" value={rehab} onChange={setRehab} testid="cash-repairs" />
      </div>
      <div className="mt-4">
        <Field label="Assignment Fee ($)" value={fee} onChange={setFee} testid="cash-fee" />
      </div>
      <SellerFields seller={seller} setSeller={setSeller} street={street} setStreet={setStreet} prefix="cash" />
      <Actions onCalc={calc} onClear={clear} prefix="cash" />
      {result && <ResultPanel key={result.offer} prefix="cash" {...result} />}
    </div>
  );
}

const CR_DOWN_PCT = 0.05, CR_AM_YEARS = 30;

function CreativeCalc() {
  const [mode, setMode] = useState("seller"); // seller | subto
  return (
    <div>
      <div className="flex justify-center gap-2 mb-6">
        {[{ id: "seller", label: "Seller Finance" }, { id: "subto", label: "Mortgage Takeover" }].map((m) => (
          <button
            key={m.id}
            onClick={() => setMode(m.id)}
            className="px-4 py-1.5 rounded-full text-xs font-medium transition"
            style={{
              background: mode === m.id ? "var(--lime-glow)" : "var(--surface2)",
              color: mode === m.id ? "var(--lime)" : "var(--text-muted)",
              border: mode === m.id ? "1px solid var(--border-lime)" : "1px solid var(--border)",
            }}
            data-testid={`creative-mode-${m.id}`}
          >
            {m.label}
          </button>
        ))}
      </div>
      {mode === "seller" ? <SellerFinanceCalc /> : <SubToCalc />}
    </div>
  );
}

function RangeControl({ label, value, min, max, step, suffix, onChange, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="field-label mb-0">{label}</label>
        <span className="font-mono text-xs" style={{ color: "var(--lime)" }}>{value}{suffix}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full cursor-pointer"
        style={{ accentColor: "var(--lime)" }}
        data-testid={testid}
      />
    </div>
  );
}

function SellerFinanceCalc() {
  const [ask, setAsk] = useState("");
  const [fee, setFee] = useState("");
  const [rate, setRate] = useState(0);
  const [crArv, setCrArv] = useState("");
  const [rent, setRent] = useState("");
  const [seller, setSeller] = useState("");
  const [street, setStreet] = useState(""); // % annual, 0-10
  const [balloonYears, setBalloonYears] = useState(10); // 3-30
  const [result, setResult] = useState(null);

  const calc = () => {
    const a = num(ask), f = num(fee);
    const priceAt = (prem) => Math.max(0, a * prem - f);
    const price = priceAt(1.10);
    const down = price * CR_DOWN_PCT;
    const financed = Math.max(0, price - down);
    const r = rate / 100 / 12;
    const n = CR_AM_YEARS * 12;
    const k = Math.min(balloonYears, CR_AM_YEARS) * 12;
    const payment = r > 0 ? (financed * r) / (1 - Math.pow(1 + r, -n)) : financed / n;
    const balloonDue = r > 0
      ? financed * Math.pow(1 + r, k) - (payment * (Math.pow(1 + r, k) - 1)) / r
      : Math.max(0, financed - payment * k);
    setResult({
      offer: price,
      range: [
        { label: "Aggressive", note: "105%", value: priceAt(1.05) },
        { label: "Standard", note: "110%", value: price },
        { label: "Stretch", note: "115%", value: priceAt(1.15) },
      ],
      breakdown: { price, down, financed, payment, balloonDue, rate, balloonYears, arv: num(crArv), rent: num(rent), cashFlow: num(rent) > 0 ? num(rent) - payment : 0 },
      script: `${hey(seller)}so I sent ${place(street)} over to my underwriting team, and they found a way to get you more than a straight cash offer. They approved ${fmt(price)} total. You'd get ${fmt(down)} at closing, then ${fmt(payment)} every month${rate > 0 ? ` at ${rate}% interest` : ""}, and the rest, ${fmt(balloonDue)}, gets paid off in full by year ${balloonYears}. So you get the bigger number, you just get it over time instead of all at once. How does that sound?`,
      aiPayload: { calc_type: "creative", inputs: { Strategy: "Seller finance", ARV: num(crArv), "Market Rent": num(rent), "Seller Asking Price": a, "Assignment Fee": f, "Purchase Price (110%)": price, "Down Payment (5%)": down, "Interest Rate": `${rate}%`, "Monthly Payment": Math.round(payment * 100) / 100, "Balloon Year": balloonYears, "Balloon Due": Math.round(balloonDue), Term: `${CR_AM_YEARS} yr amort` }, max_offer: price },
    });
  };
  const clear = () => { setAsk(""); setFee(""); setRate(0); setBalloonYears(10); setSeller(""); setStreet(""); setResult(null); };

  return (
    <div className="surface-card p-6 sm:p-8" data-testid="creative-calc">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Seller's Asking Price ($)" value={ask} onChange={setAsk} testid="creative-ask" />
        <Field label="Assignment Fee ($)" value={fee} onChange={setFee} testid="creative-fee" />
        <Field label="ARV ($)" value={crArv} onChange={setCrArv} testid="creative-arv" />
        <Field label="Market Rent ($/mo)" value={rent} onChange={setRent} testid="creative-rent" />
      </div>
      <div className="grid sm:grid-cols-2 gap-6 mt-5">
        <RangeControl label="Interest Rate" value={rate} min={0} max={10} step={0.25} suffix="%" onChange={setRate} testid="creative-rate-slider" />
        <RangeControl label="Balloon Year" value={balloonYears} min={3} max={30} step={1} suffix=" yrs" onChange={setBalloonYears} testid="creative-balloon-slider" />
      </div>
      <SellerFields seller={seller} setSeller={setSeller} street={street} setStreet={setStreet} prefix="creative" />
      <Actions onCalc={calc} onClear={clear} prefix="creative" />
      {result && (
        <ResultPanel key={`${result.offer}-${result.breakdown.rate}-${result.breakdown.balloonYears}`} prefix="creative" offer={result.offer} range={result.range} script={result.script} aiPayload={result.aiPayload}>
          <div className="mt-4 space-y-2" data-testid="creative-breakdown">
            <Row label="Purchase Price" v={fmt(result.breakdown.price)} />
            <Row label="Down Payment" v={fmt(result.breakdown.down)} />
            <Row label="Financed Amount" v={fmt(result.breakdown.financed)} />
            <Row label="Monthly Payment (P&I)" v={fmt(result.breakdown.payment)} accent />
            <Row label={`Balloon Due (Year ${result.breakdown.balloonYears})`} v={fmt(result.breakdown.balloonDue)} accent />
            <Row label="Term" v={`30 yr amort • ${result.breakdown.balloonYears} yr balloon @ ${result.breakdown.rate}%`} />
            {result.breakdown.arv > 0 && (
              <Row label="Price as % of ARV" v={`${Math.round((result.breakdown.price / result.breakdown.arv) * 100)}%`} />
            )}
            {result.breakdown.rent > 0 && (
              <Row
                label="End buyer cash flow / mo"
                v={`${result.breakdown.cashFlow >= 0 ? "" : "-"}${fmt(Math.abs(result.breakdown.cashFlow))}`}
                accent
              />
            )}
          </div>
        </ResultPanel>
      )}
    </div>
  );
}

function SubToCalc() {
  const [balance, setBalance] = useState("");
  const [piti, setPiti] = useState("");
  const [arrears, setArrears] = useState("");
  const [seller, setSeller] = useState("");
  const [street, setStreet] = useState("");
  const [cash, setCash] = useState("");
  const [fee, setFee] = useState("");
  const [result, setResult] = useState(null);

  const calc = () => {
    const b = num(balance), p = num(piti), ar = num(arrears), c = num(cash), f = num(fee);
    const entry = c + ar + f;
    const totalPrice = b + c + ar + f;
    setResult({
      offer: entry,
      rows: { piti: p, totalPrice, balance: b },
      script: `${hey(seller)}I talked to my underwriting team about ${place(street)}, and here's what they approved. We'd take over your monthly payment of ${fmt(p)} starting at closing${ar > 0 ? `, catch up the ${fmt(ar)} you're behind` : ""}${c > 0 ? `, and put ${fmt(c)} in your pocket when we close` : ""}. The loan stays in your name until it's paid off or refinanced, so we'll send you proof every month that the payment went out. That way you always know it's handled. Does that work for you?`,
      aiPayload: { calc_type: "creative", inputs: { Strategy: "Mortgage takeover (Subject-To)", "Existing Loan Balance": b, "Monthly Payment (PITI)": p, "Back Payments / Arrears": ar, "Cash to Seller": c, "Assignment Fee": f, "Total Entry Cost": entry, "Total Purchase Price": totalPrice }, max_offer: totalPrice },
    });
  };
  const clear = () => { setBalance(""); setPiti(""); setArrears(""); setCash(""); setFee(""); setSeller(""); setStreet(""); setResult(null); };

  return (
    <div className="surface-card p-6 sm:p-8" data-testid="subto-calc">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Existing Loan Balance ($)" value={balance} onChange={setBalance} testid="subto-balance" />
        <Field label="Current Monthly Payment, PITI ($)" value={piti} onChange={setPiti} testid="subto-piti" />
        <Field label="Back Payments / Arrears ($)" value={arrears} onChange={setArrears} testid="subto-arrears" />
        <Field label="Cash to Seller ($)" value={cash} onChange={setCash} testid="subto-cash" />
      </div>
      <div className="mt-4">
        <Field label="Assignment Fee ($)" value={fee} onChange={setFee} testid="subto-fee" />
      </div>
      <SellerFields seller={seller} setSeller={setSeller} street={street} setStreet={setStreet} prefix="subto" />
      <Actions onCalc={calc} onClear={clear} prefix="subto" />
      {result && (
        <ResultPanel key={result.offer} prefix="subto" title="Total Entry Cost" offer={result.offer} script={result.script} aiPayload={result.aiPayload}>
          <div className="mt-4 space-y-2" data-testid="subto-breakdown">
            <Row label="Monthly Payment You Take Over" v={fmt(result.rows.piti)} accent />
            <Row label="Loan Balance Taken Over" v={fmt(result.rows.balance)} />
            <Row label="Total Price, Incl. Your Fee" v={fmt(result.rows.totalPrice)} accent />
          </div>
          <p className="text-xs mt-3" style={{ color: "var(--text-muted)" }} data-testid="subto-disclosure">
            Tell the seller the truth: almost every mortgage has a due-on-sale clause, so the lender can call the
            loan when title transfers. Never promise their credit is protected or that payments are guaranteed.
            Put who pays what in writing and have a title company or attorney review it.
          </p>
        </ResultPanel>
      )}
    </div>
  );
}

function LandCalc() {
  const [mv, setMv] = useState("");
  const [seller, setSeller] = useState("");
  const [street, setStreet] = useState("");
  const [fee, setFee] = useState("");
  const [result, setResult] = useState(null);

  const calc = () => {
    const m = num(mv), f = num(fee);
    const offers = [0.15, 0.20, 0.25].map((p) => m * p - f);
    setResult({
      offer: offers[1],
      range: [
        { label: "Aggressive", note: "15%", value: offers[0] },
        { label: "Standard", note: "20%", value: offers[1] },
        { label: "Stretch", note: "25%", value: offers[2] },
      ],
      script: `${hey(seller)}I sent ${place(street, "lot")} over to my underwriting team. They look at what builders in the area are paying for land, and they got you approved for ${fmt(offers[0])} cash, as-is. No agent fees, and we can close on your timeline. How does that sound?`,
      aiPayload: { calc_type: "land", inputs: { "New Build ARV": m, "Assignment Fee": f }, max_offer: offers[1] },
    });
  };
  const clear = () => { setMv(""); setFee(""); setSeller(""); setStreet(""); setResult(null); };

  return (
    <div className="surface-card p-6 sm:p-8" data-testid="land-calc">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="New Build ARV ($)" value={mv} onChange={setMv} testid="land-arv" />
        <Field label="Assignment Fee ($)" value={fee} onChange={setFee} testid="land-fee" />
      </div>
      <SellerFields seller={seller} setSeller={setSeller} street={street} setStreet={setStreet} prefix="land" />
      <Actions onCalc={calc} onClear={clear} prefix="land" />
      {result && <ResultPanel key={result.offer} prefix="land" {...result} />}
    </div>
  );
}

function ZillowCalc() {
  const [zest, setZest] = useState("");
  const [condition, setCondition] = useState("Medium Rehab");
  const [seller, setSeller] = useState("");
  const [street, setStreet] = useState("");
  const [result, setResult] = useState(null);

  const calc = () => {
    const z = num(zest);
    const bump = { "Turnkey": 0.08, "Light Rehab": 0.04, "Medium Rehab": 0, "Heavy Rehab": -0.05, "Full Rehab": -0.1 }[condition] || 0;
    const offers = [0.60 + bump, 0.645 + bump, 0.70 + bump].map((p) => z * Math.max(0.2, p));
    setResult({
      offer: offers[1],
      range: [
        { label: "Aggressive", note: "60%", value: offers[0] },
        { label: "Standard", note: "64.5%", value: offers[1] },
        { label: "Stretch", note: "70%", value: offers[2] },
      ],
      script: `${hey(seller)}I ran ${place(street)} by my underwriting team real quick, and off the numbers I have so far, they're putting it somewhere around ${fmt(offers[0])}. Before they lock in a real offer for you, I want to make sure I've got the condition right. Walk me through it. What would you say needs work?`,
      aiPayload: { calc_type: "zillow", inputs: { Zestimate: z, Condition: condition }, max_offer: offers[1] },
    });
  };
  const clear = () => { setZest(""); setSeller(""); setStreet(""); setCondition("Medium Rehab"); setResult(null); };

  return (
    <div className="surface-card p-6 sm:p-8" data-testid="zillow-calc">
      <Field label="Zestimate ($) — ballpark only" value={zest} onChange={setZest} testid="zillow-zest" />
      <SelectField
        label="Condition"
        value={condition}
        onChange={setCondition}
        options={["Turnkey", "Light Rehab", "Medium Rehab", "Heavy Rehab", "Full Rehab"]}
        testid="zillow-condition"
        hint="A Zestimate doesn't know the condition. Use this to get the seller talking about repairs."
      />
      <SellerFields seller={seller} setSeller={setSeller} street={street} setStreet={setStreet} prefix="zillow" />
      <Actions onCalc={calc} onClear={clear} prefix="zillow" />
      {result && <ResultPanel key={result.offer} prefix="zillow" {...result} />}
    </div>
  );
}

function Row({ label, v, accent }) {
  return (
    <div className="calc-row flex items-center justify-between px-4 py-2.5 rounded-2xl" style={{ background: "var(--surface2)", border: "1px solid var(--border)" }}>
      <span className="text-sm" style={{ color: "var(--text-muted)" }}>{label}</span>
      <span className="font-mono text-sm" style={{ color: accent ? "var(--lime)" : "var(--text)" }}>{v}</span>
    </div>
  );
}
